import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";

const LINKS = [
  { label: "About", href: "#about", id: "nav-link-about" },
  { label: "Services", href: "#services", id: "nav-link-services" },
  { label: "Location", href: "#location", id: "nav-link-location" },
  { label: "Contact", href: "#contact", id: "nav-link-contact" },
];

const scrollTo = (href) => {
  window.__lenis?.scrollTo(href, { offset: -64, duration: 1.2 });
};

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const isHome = pathname === "/";

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const handleAnchor = (e, href) => {
    e.preventDefault();
    setOpen(false);
    if (isHome) scrollTo(href);
    else window.location.href = `/${href}`;
  };

  return (
    <header
      data-testid="nav-header"
      className="fixed inset-x-0 top-0 z-50 border-b border-black/5 bg-white/80 backdrop-blur-xl"
    >
      <div className="mx-auto flex h-14 max-w-5xl items-center justify-between px-5 sm:px-6">
        <Link to="/" data-testid="nav-logo-link" className="flex items-center gap-2.5" onClick={() => setOpen(false)}>
          <img src="/meranggi-logo.png" alt="Meranggi logo" className="h-6 w-6 rounded-full object-cover" />
          <span className="text-sm font-semibold tracking-tight text-[#1d1d1f]">Meranggi</span>
        </Link>

        <nav className="hidden items-center gap-8 md:flex" data-testid="nav-desktop-links">
          {LINKS.map((l) => (
            <a
              key={l.id}
              href={l.href}
              data-testid={l.id}
              onClick={(e) => handleAnchor(e, l.href)}
              className="text-xs font-normal text-[#1d1d1f]/80 transition-colors duration-200 hover:text-[#1d1d1f]"
            >
              {l.label}
            </a>
          ))}
          <a
            href="mailto:info@meranggi.com"
            data-testid="nav-cta-contact-button"
            className="rounded-full bg-[#0066cc] px-4 py-1.5 text-xs font-medium text-white transition-colors duration-200 hover:bg-[#0077ed]"
          >
            Contact Us
          </a>
        </nav>

        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen((v) => !v)}
          className="flex h-9 w-9 items-center justify-center text-[#1d1d1f] md:hidden"
          aria-label="Toggle menu"
        >
          {open ? <X size={20} strokeWidth={1.5} /> : <Menu size={20} strokeWidth={1.5} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="nav-mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="absolute inset-x-0 top-full min-h-[calc(100svh-3.5rem)] bg-white md:hidden"
          >
            <div className="flex flex-col px-8 pt-8">
              {LINKS.map((l, i) => (
                <motion.a
                  key={l.id}
                  href={l.href}
                  data-testid={`${l.id}-mobile`}
                  onClick={(e) => handleAnchor(e, l.href)}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.05 * i, duration: 0.35 }}
                  className="border-b border-black/5 py-5 text-2xl font-semibold tracking-tight text-[#1d1d1f]"
                >
                  {l.label}
                </motion.a>
              ))}
              <motion.a
                href="mailto:info@meranggi.com"
                data-testid="nav-cta-contact-button-mobile"
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.25, duration: 0.35 }}
                className="mt-8 rounded-full bg-[#0066cc] px-6 py-3.5 text-center text-sm font-medium text-white"
              >
                Contact Us
              </motion.a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
